import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Container, Typography, Skeleton, Button } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useAuth } from "../hooks/useAuth";
import {
  useLazyGetOverviewDataQuery,
  useLazyGetTeamsQuery,
} from "../store/api";
import { extractList } from "../utils/response";
import { useLazySelectOptions } from "../hooks/useLazySelectOptions";
import PageLayout from "../components/layout/PageLayout";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { DashboardView } from "../components/dashboard";
import { DashboardDrilldownContext } from "../components/dashboard/dashboardDrilldown";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

/** Per-team rows from the API, with the field aliases the team table/chart read. */
const mapTeams = (teams) =>
  teams.map((t) => ({
    id: t.team_id ?? t.id,
    name: t.team_name ?? t.name,
    total_leads: t.total_leads,
    sales_generated: t.sales_generated,
    declined: t.declined,
    in_process: t.in_process,
    not_interested: t.not_interested,
    follow_up: t.follow_up,
    pending: t.pending,
    orders_total: t.orders_total,
    revenue: t.revenue,
    conversion_rate:
      t.conversion_rate != null ? Number(t.conversion_rate) : null,
  }));

const Dashboard = () => {
  const [fetchOverviewData] = useLazyGetOverviewDataQuery();
  const [fetchTeams] = useLazyGetTeamsQuery();
  const teamsSelect = useLazySelectOptions(fetchTeams, {
    mapResult: (res) => extractList(res),
  });
  const { user, hasPermission } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filters the dashboard was last loaded with — dashboard cards link to lists using these.
  const [appliedFilters, setAppliedFilters] = useState({});

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [teamFilterId, setTeamFilterId] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The API shapes the payload per role; the only role left here is the drill-down scope:
  // a Closer's card links must list only their own records.
  const userRole = user?.roles?.[0]?.name;
  // const isCloser =
  //   userRole === "Closer" && userRole !== "Admin" && !hasPermission("full-access");

  const drilldownContext = useMemo(
    () => ({ filters: appliedFilters }), //, scopeToUser: isCloser
    [appliedFilters], //, isCloser
  );

  const fetchOverview = useCallback(
    async (params) => {
      try {
        setLoading(true);
        const res = await fetchOverviewData(params ?? {}).unwrap();
        setOverview(res);
        setAppliedFilters({
          date_from: params?.date_from || "",
          date_to: params?.date_to || "",
          team_id: params?.team_id || "",
        });
      } catch {
        // keep previous overview on failure
      } finally {
        setLoading(false);
      }
    },
    [fetchOverviewData],
  );

  const data = useMemo(() => {
    const body = overview?.data ?? overview ?? {};
    // Older payloads nest the org-wide blocks under `reports`.
    const raw = body.reports ? { ...body, ...body.reports } : body;
    return {
      ...raw,
      teams: Array.isArray(raw.teams) ? mapTeams(raw.teams) : undefined,
      totalUsers: raw.total_active_users ?? raw.total_users,
      agentCallReport: raw.agentCallReport,
      monthlySales: Array.isArray(raw.monthlySales)
        ? raw.monthlySales
        : undefined,
    };
  }, [overview]);

  const hasTeamFilter = Array.isArray(data.teams);

  const applyFilters = useCallback(() => {
    fetchOverview({
      date_from: dateFrom.trim(),
      date_to: dateTo.trim(),
      // Only sent when the dashboard offers a team filter (the API ignores it otherwise).
      ...(hasTeamFilter && {
        team_id: teamFilterId !== "" ? String(teamFilterId) : "",
      }),
    });
  }, [fetchOverview, dateFrom, dateTo, teamFilterId, hasTeamFilter]);

  const clearFilters = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    setTeamFilterId("");
    fetchOverview({});
  }, [fetchOverview]);

  useEffect(() => {
    fetchOverview({});
  }, [fetchOverview]);

  const loadTeamsForFilters = useCallback(
    () => teamsSelect.load().catch(() => []),
    [teamsSelect],
  );

  useEffect(() => {
    if (hasTeamFilter && filtersOpen) {
      void loadTeamsForFilters();
    }
  }, [hasTeamFilter, filtersOpen, loadTeamsForFilters]);

  return (
    <PageLayout>
      <DashboardDrilldownContext.Provider value={drilldownContext}>
        <Container
          maxWidth="xl"
          sx={{
            p: { xs: 2, sm: 3 },
            width: 1,
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: "grey.800", mb: 1 }}
              >
                {loading ? (
                  <Skeleton width={300} />
                ) : (
                  `Welcome back, ${user?.name || "User"}!`
                )}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {loading ? (
                  <Skeleton width={400} />
                ) : (
                  "Here's your performance at a glance."
                )}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
              sx={{
                flexShrink: 0,
                borderColor: "#154A8A",
                color: "#154A8A",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#0d3a6a",
                  bgcolor: "rgba(21, 74, 138, 0.06)",
                },
              }}
            >
              {filtersOpen ? "Hide filters" : "Filter"}
            </Button>
          </Box>

          <DashboardView
            data={data}
            loading={loading}
            showFilters={filtersOpen}
            dateFrom={dateFrom}
            dateTo={dateTo}
            teamFilterId={teamFilterId}
            teamOptions={teamsSelect.options}
            teamsLoading={teamsSelect.loading}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onTeamChange={setTeamFilterId}
            onTeamOpen={loadTeamsForFilters}
            onApply={applyFilters}
            onClearFilters={clearFilters}
          />
        </Container>
      </DashboardDrilldownContext.Provider>
    </PageLayout>
  );
};

export default Dashboard;
